// Ported verbatim from the LeadMailer Design System (components/navigation/Topbar.jsx).
// On mobile shows a menu button that opens a drawer with the same nav items.
import React from 'react'
import type { NavItem } from './Sidebar'

export interface TopbarProps {
  title?: React.ReactNode
  actions?: React.ReactNode
  items?: NavItem[]
  active?: string
  onSelect?: (id: string) => void
  showMenu?: boolean
  style?: React.CSSProperties
}

export function Topbar({
  title,
  actions,
  items = [],
  active,
  onSelect,
  showMenu,
  style,
}: TopbarProps) {
  const [open, setOpen] = React.useState(false)
  return (
    <>
      <header
        className="lm-glass-flat"
        style={{
          display: 'flex',
          alignItems: 'center',
          // Screens can push several actions in here (the template editor adds
          // a mode toggle, Cancel, Show preview and Save). On a phone that runs
          // past the right edge and Save becomes unreachable, so the bar is
          // allowed to grow a second row instead of clipping.
          flexWrap: 'wrap',
          gap: 12,
          minHeight: 56,
          padding: '8px 16px',
          background: 'var(--surface-topbar)',
          borderBottom: '1px solid var(--border-chrome)',
          position: 'sticky',
          top: 0,
          zIndex: 20,
          ...style,
        }}
      >
        {showMenu && (
          <button
            aria-label="Menu"
            onClick={() => setOpen(true)}
            style={{
              width: 44,
              height: 44,
              flex: 'none',
              marginLeft: -8,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            >
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}
        <div
          style={{
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
            letterSpacing: 'var(--tracking-tight)',
            color: 'var(--text-primary)',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            minWidth: 0,
          }}
        >
          {actions}
        </div>
      </header>

      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 90, background: 'var(--surface-overlay)' }}
        >
          <nav
            className="lm-glass"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              width: 280,
              maxWidth: '85vw',
              background: 'var(--surface-sidebar)',
              borderRight: '1px solid var(--border-chrome)',
              boxShadow: 'var(--shadow-overlay)',
              padding: '16px 10px',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <div
              style={{
                fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                padding: '4px 10px 14px',
                color: 'var(--text-primary)',
              }}
            >
              LeadMailer
            </div>
            {items.map((it) => (
              <button
                key={it.id}
                onClick={() => {
                  onSelect && onSelect(it.id)
                  setOpen(false)
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  minHeight: 44,
                  padding: '0 10px',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-md)',
                  textAlign: 'left',
                  color: active === it.id ? 'var(--nav-active-text)' : 'var(--text-secondary)',
                  background: active === it.id ? 'var(--nav-active-bg)' : 'transparent',
                  boxShadow: active === it.id ? 'inset 3px 0 0 var(--nav-active-bar)' : 'none',
                }}
              >
                {it.icon && (
                  <span
                    style={{
                      display: 'inline-flex',
                      width: 18,
                      color: active === it.id ? 'var(--nav-icon-active)' : 'var(--nav-icon)',
                    }}
                  >
                    {it.icon}
                  </span>
                )}
                {it.label}
              </button>
            ))}
          </nav>
        </div>
      )}
    </>
  )
}
